/**
 * Minimal app state: theme (light "editorial" / dark "instrument") + the current
 * scan result + the user's cohort/persona from the quiz. No browser storage is
 * used (results live in memory for the session, privacy parity with the app;
 * the raw image never leaves the browser, §11.3).
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Cohort, Persona, ScanResult } from '@facet/engine';
import { track, EVENTS } from '../lib/analytics';
import { restoreSession } from '../lib/auth';
import { syncAccountHistory } from '../lib/historySync';

type ThemeName = 'light' | 'dark';

// The current result lives in memory, but we ALSO mirror it into sessionStorage so
// a page reload of /report or /plan keeps the report instead of dropping to the
// "no scan yet" state. This is derived-only (scores, metrics, tier, plan, skin) —
// no photo and no landmarks are ever in a ScanResult, so it carries the same
// privacy posture as the local history. sessionStorage (not localStorage) means it
// clears when the tab closes; the persistent progress trend still lives in history.
const RESULT_KEY = 'facet.result.v1';
interface CachedResult {
  result: ScanResult;
  cohort: Cohort | null;
}
function readResultCache(): CachedResult | null {
  try {
    const raw = window.sessionStorage.getItem(RESULT_KEY);
    return raw ? (JSON.parse(raw) as CachedResult) : null;
  } catch {
    return null;
  }
}
/** Drop the cached in-session result (called on clear/delete for privacy hygiene). */
export function clearResultCache(): void {
  try {
    window.sessionStorage.removeItem(RESULT_KEY);
  } catch {
    /* ignore */
  }
}

interface AppState {
  theme: ThemeName;
  toggleTheme: () => void;
  cohort: Cohort | null;
  setCohort: (c: Cohort) => void;
  persona: Persona;
  setPersona: (p: Persona) => void;
  result: ScanResult | null;
  setResult: (r: ScanResult | null) => void;
  concerns: string[];
  setConcerns: (c: string[]) => void;
}

const Ctx = createContext<AppState | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<ThemeName>('light');
  // Rehydrate the last result + cohort from sessionStorage so a reload keeps the report.
  const [cohort, setCohort] = useState<Cohort | null>(() => readResultCache()?.cohort ?? null);
  const [persona, setPersona] = useState<Persona>('neutral');
  const [result, setResult] = useState<ScanResult | null>(() => readResultCache()?.result ?? null);
  const [concerns, setConcerns] = useState<string[]>([]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Mirror the result to sessionStorage on change (or clear it when there is none).
  useEffect(() => {
    if (result) {
      try {
        window.sessionStorage.setItem(RESULT_KEY, JSON.stringify({ result, cohort }));
      } catch {
        /* private mode / quota — the report just won't survive a reload */
      }
    } else {
      clearResultCache();
    }
  }, [result, cohort]);

  // Re-hydrate an account session from a stored token, if accounts are enabled,
  // then pull this account's history so progress is present on every device.
  useEffect(() => {
    void restoreSession().then(() => void syncAccountHistory());
  }, []);

  // Capture an inbound referral (?ref=code) once, for attribution (§4.15).
  useEffect(() => {
    try {
      const ref = new URLSearchParams(window.location.search).get('ref');
      if (ref) {
        window.localStorage.setItem('facet.ref', ref);
        track(EVENTS.referralVisit, { ref });
      }
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo<AppState>(
    () => ({
      theme,
      toggleTheme: () => setTheme((t) => (t === 'light' ? 'dark' : 'light')),
      cohort,
      setCohort,
      persona,
      setPersona,
      result,
      setResult,
      concerns,
      setConcerns,
    }),
    [theme, cohort, persona, result, concerns],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp(): AppState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
